import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { Fragment as ReactFragment } from "react";

import { v4Date, v4Money, v4Number, v4Quantity } from "../v4Explanation";
import { isV4Expired } from "../v4ReferenceState";
import V4DecisionSummary from "./V4DecisionSummary";
import V4HypothesisReview from "./V4HypothesisReview";

export { v4Date, v4Money } from "../v4Explanation";

const STATUS = {
  pending: "Aguardando decisão do V4",
  insufficient_data: "Dados insuficientes",
  rejected: "Sugestão não aprovada",
  error: "V4 indisponível",
  ready: "Sugestão disponível"
};
const PILOT_STATUS = {
  not_configured: "Piloto V4 não ativado",
  disabled: "Piloto V4 desativado",
  ended: "Janela do piloto encerrada",
  budget_exhausted: "Limite de chamadas atingido",
  scheduled: "Piloto V4 programado",
  active_window: "Aguardando decisão do V4"
};
export const v4StatusLabel = suggestion =>
  isV4Expired(suggestion)
    ? "Referência vencida"
    : (suggestion?.status === "pending" &&
        PILOT_STATUS[suggestion?.pilot_state]) ||
      (suggestion?.status === "pending" &&
        suggestion?.failure_code === "policy_budget_window_or_slot_blocked" &&
        "Não concluído nesta rodada") ||
      STATUS[suggestion?.status] ||
      "Aguardando decisão do V4";
const text = value => (typeof value === "string" ? value : null);
const list = value => (Array.isArray(value) ? value : []);
const priceVariation = portion => {
  const current = v4Number(portion?.current_price_brl);
  const suggested = v4Number(portion?.suggested_price_brl);
  if (current === null || current <= 0 || suggested === null) return "—";
  const percentage = (suggested / current - 1) * 100;
  return Number.isFinite(percentage)
    ? `${percentage.toLocaleString("pt-BR", {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2
      })}%`
    : "—";
};
const unitPrice = (portion, field) => {
  const size = v4Number(portion?.portion_base);
  const price = v4Number(portion?.[field]);
  return size > 0 && price !== null && price >= 0 ? v4Money(price / size) : "—";
};
const Section = ({ title, children, collapsible = false, summary }) =>
  collapsible ? (
    <Accordion elevation={0} square style={{ marginTop: 16 }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`v4-${title.replace(/\s+/g, "-")}-content`}
        id={`v4-${title.replace(/\s+/g, "-")}-summary`}
        style={{ paddingLeft: 0 }}
      >
        <Box>
          <Typography
            component="h3"
            variant="subtitle1"
            style={{ fontWeight: 600 }}
          >
            {title}
          </Typography>
          {summary ? (
            <Typography variant="body2" color="textSecondary">
              {summary}
            </Typography>
          ) : null}
        </Box>
      </AccordionSummary>
      <AccordionDetails style={{ display: "block", padding: "0 0 16px" }}>
        {children}
      </AccordionDetails>
    </Accordion>
  ) : (
    <Box mt={3}>
      <Typography
        component="h3"
        variant="subtitle1"
        style={{ fontWeight: 600, marginBottom: 8 }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
const Note = ({ children }) => (
  <Typography variant="body2" paragraph style={{ overflowWrap: "anywhere" }}>
    {children}
  </Typography>
);

function CatalogComparison({ state, catalogId, catalogIds }) {
  const catalogs = [...new Set([Number(catalogId), ...catalogIds.map(Number)])];
  const details = state.detail.catalogs || {
    [catalogId]: state.detail
  };
  const portions = new Map();
  catalogs.forEach(catalog => {
    if (details[catalog]?.status !== "ready") return;
    list(details[catalog].data?.portions).forEach((portion, index) => {
      const key = `${
        portion.product_id || portion.sku || `unknown-${catalog}-${index}`
      }|${portion.portion_base}`;
      const row = portions.get(key) || { portion, prices: {} };
      row.prices[catalog] = portion;
      portions.set(key, row);
    });
  });
  return (
    <Section title="Preços das embalagens">
      <Note>
        Preços do catálogo usados na análise e sugestão V4. O total é o valor da
        embalagem; o preço por unidade é o total dividido pelas porções base.
      </Note>
      {catalogs
        .filter(catalog => catalog !== Number(catalogId))
        .map(catalog => (
          <Note key={catalog}>
            Catálogo {catalog}:{" "}
            {details[catalog]?.status === "error"
              ? "Não foi possível consultar este catálogo."
              : details[catalog]?.status === "ready"
              ? v4StatusLabel(details[catalog].data)
              : "Carregando detalhes…"}
          </Note>
        ))}
      {portions.size ? (
        <TableContainer style={{ overflowX: "auto" }}>
          <Table
            size="small"
            aria-label="Preços V4 por catálogo e porção"
            style={{ minWidth: catalogs.length > 1 ? 1000 : 680 }}
          >
            <TableHead>
              {catalogs.length > 1 ? (
                <TableRow>
                  <TableCell>Porção</TableCell>
                  {catalogs.map(catalog => (
                    <TableCell key={catalog} align="center" colSpan={5}>
                      Catálogo {catalog}
                    </TableCell>
                  ))}
                </TableRow>
              ) : null}
              <TableRow>
                <TableCell>
                  {catalogs.length === 1 ? "SKU / embalagem" : ""}
                </TableCell>
                {catalogs.map(catalog => (
                  <ReactFragment key={catalog}>
                    <TableCell align="right">Na análise (total)</TableCell>
                    <TableCell align="right">Na análise / unidade</TableCell>
                    <TableCell align="right">Sugestão (total)</TableCell>
                    <TableCell align="right">Sugestão / unidade</TableCell>
                    <TableCell align="right">Variação</TableCell>
                  </ReactFragment>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {[...portions.entries()]
                .sort(
                  (a, b) =>
                    Number(a[1].portion.portion_base) -
                    Number(b[1].portion.portion_base)
                )
                .map(([key, { portion, prices }], index) => (
                  <TableRow key={key}>
                    <TableCell>
                      {text(portion.name) ||
                        text(portion.label) ||
                        (portion.portion_base != null
                          ? `${v4Quantity(portion.portion_base)} ${
                              Number(portion.portion_base) === 1
                                ? "porção"
                                : "porções"
                            } base`
                          : `Porção ${index + 1}`)}
                      <Typography
                        variant="caption"
                        display="block"
                        color="textSecondary"
                      >
                        {portion.sku
                          ? `SKU ${portion.sku}`
                          : portion.product_id
                          ? `ID do produto ${portion.product_id}`
                          : "não informado"}
                      </Typography>
                    </TableCell>
                    {catalogs.map(catalog => (
                      <ReactFragment key={catalog}>
                        <TableCell
                          align="right"
                          style={{ whiteSpace: "nowrap" }}
                        >
                          {v4Money(prices[catalog]?.current_price_brl)}
                        </TableCell>
                        <TableCell
                          align="right"
                          style={{ whiteSpace: "nowrap" }}
                        >
                          {unitPrice(prices[catalog], "current_price_brl")}
                        </TableCell>
                        <TableCell
                          align="right"
                          style={{ whiteSpace: "nowrap" }}
                        >
                          {v4Money(prices[catalog]?.suggested_price_brl)}
                        </TableCell>
                        <TableCell
                          align="right"
                          style={{ whiteSpace: "nowrap" }}
                        >
                          {unitPrice(prices[catalog], "suggested_price_brl")}
                        </TableCell>
                        <TableCell align="right">
                          {priceVariation(prices[catalog])}
                        </TableCell>
                      </ReactFragment>
                    ))}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Note>Preços por porção ainda não disponíveis.</Note>
      )}
      {catalogs
        .filter(catalog => catalog !== Number(catalogId))
        .map(catalog => {
          const detail = details[catalog];
          if (detail?.status === "error")
            return (
              <Button key={catalog} onClick={state.retryDetail} color="primary">
                Tentar consultar o catálogo {catalog} novamente
              </Button>
            );
          if (detail?.status !== "ready" || detail.data?.status !== "ready")
            return null;
          const other = detail.data;
          return (
            <Box component="details" key={catalog} mt={2}>
              <Typography component="summary" variant="body2">
                Parecer e memória do catálogo {catalog}
              </Typography>
              <Box mt={1}>
                <Note>Atualizado em {v4Date(other.generated_at)}.</Note>
                <Note>{text(other.rationale) || text(other.reason_short)}</Note>
                <Note>
                  {text(other.memory?.interpretation) ||
                    "Ainda não há resultados anteriores avaliados neste catálogo."}
                </Note>
                <V4HypothesisReview suggestion={other} />
              </Box>
            </Box>
          );
        })}
    </Section>
  );
}

export default function V4SuggestionDialog({ state, catalogId, catalogIds }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));
  const suggestion = state.detail.data;
  return (
    <Dialog
      open={Boolean(state.selected)}
      onClose={state.close}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
      aria-labelledby="v4-dialog-title"
    >
      <DialogTitle id="v4-dialog-title">
        Sug. V4 — {state.selected?.name || "Produto"}
      </DialogTitle>
      <DialogContent
        dividers
        style={{ overflowWrap: "anywhere", paddingTop: 24 }}
      >
        {state.detail.status === "loading" ? (
          <Typography role="status">Carregando detalhes V4…</Typography>
        ) : state.detail.status === "error" ? (
          <Box role="alert">
            <Note>
              Não foi possível carregar os detalhes V4. Tente novamente. Os
              preços da tabela continuam disponíveis.
            </Note>
            <Button
              variant="outlined"
              color="primary"
              onClick={state.retryDetail}
            >
              Tentar novamente
            </Button>
          </Box>
        ) : suggestion ? (
          <>
            {state.detail.refreshStatus === "loading" ? (
              <Typography role="status" variant="body2" paragraph>
                Atualizando detalhes. O parecer da última sugestão aceita
                continua disponível.
              </Typography>
            ) : state.detail.refreshStatus === "error" ? (
              <Box role="alert" mb={2}>
                <Note>
                  Falha ao atualizar os detalhes. Exibindo o parecer da última
                  sugestão aceita.
                </Note>
                <Button onClick={state.retryDetail} color="primary">
                  Tentar novamente
                </Button>
              </Box>
            ) : null}
            <V4DecisionSummary
              suggestion={suggestion}
              statusLabel={v4StatusLabel(suggestion)}
              row={state.selected?.row}
            />
            <CatalogComparison
              state={state}
              catalogId={catalogId}
              catalogIds={catalogIds}
            />
            <Section
              title="Hipóteses e memória"
              collapsible
              summary="O que testar e o que os resultados anteriores mudaram"
            >
              <V4HypothesisReview suggestion={suggestion} />
            </Section>
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={state.close} color="primary">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
